import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import {
  BookOpen,
  Plus,
  Trash,
  CheckCircle,
  FloppyDisk,
  MagnifyingGlass,
  X,
} from "@phosphor-icons/react";

export default function KnowledgeModule() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/knowledge");
      setArticles(data);
      if (selected) {
        const fresh = data.find((a) => a.article_id === selected.article_id);
        if (!fresh) {
          setSelected(null);
          setDraft(null);
        }
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        (a.body || "").toLowerCase().includes(q) ||
        (a.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [articles, search]);

  const startNew = () => {
    const blank = {
      article_id: null,
      title: "",
      body: "",
      tags: [],
      published: true,
    };
    setSelected(blank);
    setDraft(blank);
  };

  const startEdit = (a) => {
    setSelected(a);
    setDraft({ ...a, tags: a.tags || [] });
  };

  const cancel = () => {
    setSelected(null);
    setDraft(null);
  };

  const save = async () => {
    if (!draft?.title?.trim()) {
      toast.error("Title is required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: draft.title.trim(),
        body: draft.body || "",
        tags: (draft.tags || []).map((t) => t.trim()).filter(Boolean),
        published: !!draft.published,
      };
      let saved;
      if (draft.article_id) {
        const res = await api.put(`/knowledge/${draft.article_id}`, payload);
        saved = res.data;
        toast.success("Article updated");
      } else {
        const res = await api.post("/knowledge", payload);
        saved = res.data;
        toast.success("Article created");
      }
      await load();
      setSelected(saved);
      setDraft({ ...saved, tags: saved.tags || [] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!draft?.article_id) {
      cancel();
      return;
    }
    if (!window.confirm(`Delete "${draft.title}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/knowledge/${draft.article_id}`);
      toast.success("Article deleted");
      cancel();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const setTagsFromString = (str) => {
    setDraft({
      ...draft,
      tags: str
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    });
  };

  return (
    <DashboardLayout
      title="Knowledge Base"
      actions={
        <button
          onClick={startNew}
          className="btn-primary text-sm"
          data-testid="new-article-btn"
        >
          <Plus size={16} weight="bold" /> New article
        </button>
      }
    >
      <div className="grid grid-cols-12 gap-4">
        {/* List */}
        <div className="col-span-12 lg:col-span-5">
          <div className="cell-strong bg-white">
            <div className="p-4 border-b border-[var(--line)]">
              <div className="overline text-[var(--ink-soft)] mb-2">/ Articles ({articles.length})</div>
              <div className="flex items-center gap-2 input-rc !p-0 !border-[var(--line)]">
                <MagnifyingGlass size={16} weight="bold" className="ml-3 text-[var(--ink-soft)]" />
                <input
                  type="text"
                  placeholder="Search title, body, tag…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="kb-search-input"
                  className="flex-1 px-2 py-2 bg-transparent outline-none text-sm"
                />
              </div>
            </div>
            <div className="divide-y divide-[var(--line)] max-h-[640px] overflow-auto" data-testid="kb-article-list">
              {loading && <div className="p-6 text-sm text-[var(--ink-soft)]">Loading…</div>}
              {!loading && filtered.length === 0 && (
                <div className="p-6 text-sm text-[var(--ink-soft)]">
                  No articles yet. Click <strong>New article</strong> to teach your bot something.
                </div>
              )}
              {filtered.map((a) => (
                <button
                  key={a.article_id}
                  onClick={() => startEdit(a)}
                  data-testid={`kb-article-row-${a.slug}`}
                  className={`w-full text-left px-4 py-3 hover:bg-[var(--bg-soft)] transition-colors ${
                    selected?.article_id === a.article_id ? "bg-[var(--bg-soft)] border-l-4 border-[var(--brand)]" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{a.title || "(untitled)"}</div>
                      <div className="text-xs text-[var(--ink-soft)] mt-1 line-clamp-2">{a.body?.slice(0, 140)}</div>
                    </div>
                    <span
                      className={`text-[10px] font-mono-rc px-2 py-1 shrink-0 ${
                        a.published ? "bg-[var(--good)]/10 text-[var(--good)]" : "bg-[var(--ink-soft)]/10 text-[var(--ink-soft)]"
                      }`}
                    >
                      {a.published ? "LIVE" : "DRAFT"}
                    </span>
                  </div>
                  {a.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {a.tags.slice(0, 4).map((t) => (
                        <span key={t} className="text-[10px] bg-[var(--bg-soft)] px-2 py-0.5 border border-[var(--line)]">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="col-span-12 lg:col-span-7">
          {!draft ? (
            <div className="cell-strong bg-white p-12 text-center" data-testid="kb-empty-state">
              <BookOpen size={48} weight="duotone" className="mx-auto text-[var(--ink-soft)]" />
              <div className="font-display text-2xl mt-4">Train your AI on your business</div>
              <p className="text-sm text-[var(--ink-soft)] mt-2 max-w-md mx-auto">
                Add policies, FAQs, product docs — anything your customers ask. Published articles are pulled into every bot reply automatically.
              </p>
              <button onClick={startNew} className="btn-primary mt-6" data-testid="kb-empty-new-btn">
                <Plus size={16} weight="bold" /> Create your first article
              </button>
            </div>
          ) : (
            <div className="cell-strong bg-white p-6 space-y-4" data-testid="kb-editor">
              <div className="flex items-center justify-between">
                <div className="overline text-[var(--ink-soft)]">
                  / {draft.article_id ? "Edit article" : "New article"}
                </div>
                <button
                  onClick={cancel}
                  className="text-[var(--ink-soft)] hover:text-[var(--ink)] text-sm flex items-center gap-1"
                  data-testid="kb-cancel-btn"
                >
                  <X size={14} weight="bold" /> Close
                </button>
              </div>

              <div>
                <div className="overline text-[var(--ink-soft)] mb-2">Title</div>
                <input
                  className="input-rc"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="e.g. Refund policy"
                  data-testid="kb-title-input"
                />
              </div>

              <div>
                <div className="overline text-[var(--ink-soft)] mb-2">Body (Markdown supported)</div>
                <textarea
                  className="input-rc font-mono-rc text-sm"
                  rows={14}
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  placeholder={"What should the bot know?\n\nWrite full sentences — the bot reads this verbatim when answering customers."}
                  data-testid="kb-body-input"
                />
                <div className="text-xs text-[var(--ink-soft)] mt-1 font-mono-rc">
                  {(draft.body || "").length} chars
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="overline text-[var(--ink-soft)] mb-2">Tags (comma separated)</div>
                  <input
                    className="input-rc"
                    value={(draft.tags || []).join(", ")}
                    onChange={(e) => setTagsFromString(e.target.value)}
                    placeholder="pricing, billing, refund"
                    data-testid="kb-tags-input"
                  />
                </div>
                <div>
                  <div className="overline text-[var(--ink-soft)] mb-2">Status</div>
                  <label className="flex items-center gap-2 cursor-pointer select-none input-rc !py-2.5">
                    <input
                      type="checkbox"
                      checked={!!draft.published}
                      onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
                      data-testid="kb-published-toggle"
                    />
                    <span className="text-sm">
                      {draft.published ? "Published — bot uses this" : "Draft — bot ignores"}
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-[var(--line)]">
                {draft.article_id ? (
                  <button
                    onClick={remove}
                    disabled={busy}
                    className="text-[var(--bad)] hover:bg-[var(--bad)] hover:text-white border border-[var(--bad)] px-3 py-2 text-sm flex items-center gap-2 transition-colors"
                    data-testid="kb-delete-btn"
                  >
                    <Trash size={14} weight="bold" /> Delete
                  </button>
                ) : <div />}
                <div className="flex items-center gap-2">
                  <button onClick={cancel} className="btn-secondary text-sm" data-testid="kb-cancel-btn-bottom">
                    Cancel
                  </button>
                  <button
                    onClick={save}
                    disabled={busy}
                    className="btn-primary text-sm"
                    data-testid="kb-save-btn"
                  >
                    {draft.article_id ? <FloppyDisk size={16} weight="fill" /> : <CheckCircle size={16} weight="fill" />}
                    {busy ? "Saving…" : draft.article_id ? "Save changes" : "Create article"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
